// MaintenanceVendorRequestCard.jsx
// Card component for displaying maintenance vendor requests

import React from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Box,
  Typography,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  AccessTime,
  Business,
  Phone,
  Email,
  CalendarToday,
  Description,
  Delete,
} from "@mui/icons-material";
import ViewingRequestsStyles from "./ViewingRequests.styles";

const MaintenanceVendorRequestCard = ({
  request,
  vendorLinkData = null,
  onApprove,
  onDeny,
  onDelete,
}) => {
  const handleViewId = () => {
    if (request.idDocumentUrl) {
      window.open(request.idDocumentUrl, "_blank");
    } else {
      alert("No ID document available for this vendor.");
    }
  };

  const status = request.status || "pending";

  return (
    <Card sx={ViewingRequestsStyles.viewingCard}>
      <CardMedia
        component="img"
        image={request.image || "/placeholder-property.jpg"}
        alt="Property Image"
        sx={ViewingRequestsStyles.cardMedia}
      />
      <CardContent sx={ViewingRequestsStyles.cardContent}>
        {/* Status */}
        <Box sx={ViewingRequestsStyles.statusContainer}>
          {status === "approved" ? (
            <CheckCircle
              sx={{
                ...ViewingRequestsStyles.statusIcon,
                color: "#81c241",
              }}
            />
          ) : status === "denied" ? (
            <Cancel
              sx={{
                ...ViewingRequestsStyles.statusIcon,
                color: "#333333",
              }}
            />
          ) : (
            <AccessTime
              sx={{
                ...ViewingRequestsStyles.statusIcon,
                color: "#666666",
              }}
            />
          )}
          <Typography
            sx={{
              ...ViewingRequestsStyles.statusText,
              color:
                status === "approved"
                  ? "#81c241"
                  : status === "denied"
                    ? "#333333"
                    : "#666666",
            }}
          >
            {status.toUpperCase()}
          </Typography>
        </Box>

        <Divider sx={ViewingRequestsStyles.divider} />

        {/* Property Address */}
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
          Property Address
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          {request.address}
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          {request.city} {request.state}
        </Typography>

        <Divider sx={ViewingRequestsStyles.divider} />

        {/* Vendor Information */}
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
          Vendor Information
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Business fontSize="small" color="action" />
          <Typography sx={ViewingRequestsStyles.infoText}>
            {request.companyName || "N/A"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Phone fontSize="small" color="action" />
          <Typography sx={ViewingRequestsStyles.infoText}>
            {request.phone || "N/A"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Email fontSize="small" color="action" />
          <Typography sx={ViewingRequestsStyles.infoText}>
            {request.email || "N/A"}
          </Typography>
        </Box>

        <Divider sx={ViewingRequestsStyles.divider} />

        {/* Scheduled Time */}
        {request.dateDetails && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
              Scheduled Time
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Typography sx={ViewingRequestsStyles.infoText}>
                {request.dateDetails.displayText || "N/A"}
              </Typography>
            </Box>
          </>
        )}

        {/* ID Document */}
        <Divider sx={ViewingRequestsStyles.divider} />
        <Box sx={{ mt: 1, mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Description />}
            onClick={handleViewId}
            disabled={!request.idDocumentUrl}
          >
            View ID Document
          </Button>
        </Box>

        {/* Request Details Accordion */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Request Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary">
              Request ID: {request.requestId}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Submitted: {request.submittedAt ? new Date(request.submittedAt).toLocaleString() : "N/A"}
            </Typography>
            {request.reviewedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Reviewed: {new Date(request.reviewedAt).toLocaleString()}
              </Typography>
            )}

            {/* Show when request has no linkId or link data not loaded */}
            {request.linkId && !vendorLinkData && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Link details not loaded for this request.
              </Typography>
            )}
            {!request.linkId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                This request was not submitted via a generated link.
              </Typography>
            )}

            {/* Link requirements (extra details from vendor link) */}
            {vendorLinkData?.linkRequirements && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Link Requirements
                </Typography>
                {vendorLinkData.linkRequirements.twentyFourHourNotice && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    • 24 Hour Notice Minimum (Non-Emergency Tenant Occupied)
                  </Typography>
                )}
                {vendorLinkData.linkRequirements.petsOnProperty && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    • Pets on Property
                  </Typography>
                )}
                {vendorLinkData.linkRequirements.emergency && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    • Emergency: {vendorLinkData.linkRequirements.emergencyType || "Emergency maintenance"}
                  </Typography>
                )}
              </>
            )}

            {/* Tenant & property details (extra details from vendor link) */}
            {vendorLinkData?.tenantDetails && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tenant & Property Details
                </Typography>
                {vendorLinkData.tenantDetails.tenantFirstName && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    <strong>Tenant Name:</strong> {vendorLinkData.tenantDetails.tenantFirstName}
                  </Typography>
                )}
                {vendorLinkData.tenantDetails.tenantPhone && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    <strong>Tenant Phone:</strong> {vendorLinkData.tenantDetails.tenantPhone}
                  </Typography>
                )}
                {vendorLinkData.tenantDetails.tenantEmail && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    <strong>Tenant Email:</strong> {vendorLinkData.tenantDetails.tenantEmail}
                  </Typography>
                )}
                {vendorLinkData.tenantDetails.lockLocation && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    <strong>Lock Location:</strong> {vendorLinkData.tenantDetails.lockLocation}
                  </Typography>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Divider sx={{ my: 2 }} />
        {status === "pending" && (
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => onApprove(request)}
              sx={{ flex: 1 }}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onDeny(request.listingId, request.id)}
              sx={{ flex: 1 }}
            >
              Deny
            </Button>
          </Box>
        )}

        {status === "approved" && (
          <Alert severity="success" sx={{ mt: 2 }}>
            This vendor has been approved for access.
          </Alert>
        )}

        {status === "denied" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This vendor request has been denied.
          </Alert>
        )}

        {/* Action Buttons - Deny and Delete for approved status */}
        {status === "approved" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Cancel />}
              onClick={() => onDeny(request.listingId, request.id)}
            >
              Deny
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={() => onDelete(request.listingId, request.id)}
            >
              Delete Request
            </Button>
          </Box>
        )}

        {/* Delete Button - Available for denied status */}
        {status === "denied" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={() => onDelete(request.listingId, request.id)}
            >
              Delete Request
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MaintenanceVendorRequestCard;

